import React, { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { ccmApi } from '../lib/api';
import * as Review from './note-expander-review';
import { groupByPatient, flowReducer } from './note-expander-flow';
import { NoteExpanderPrefsView } from './note-expander-prefs';

// Kind → badge label + pill variant. stub/expanded are actionable; system/empty are display-only.
const BADGE = {
  stub:     { label: 'Stub',         variant: 'accent', icon: '✎' },
  expanded: { label: 'Already full', variant: '',       icon: '✓' },
  system:   { label: 'Billing',      variant: 'warn',   icon: '🔒' },
  empty:    { label: 'Empty',        variant: 'ghost',  icon: '—' },
  'auto-expanded':    { label: 'Auto-expanded',     variant: '',     icon: '⚡' },
  'auto-failed':      { label: 'Auto-expand failed', variant: 'warn', icon: '⚠' },
  // Copy mode: the original stays by design until the clinician deletes it.
  'expanded-stub-kept': { label: 'Expanded — original kept', variant: '', icon: '✓' },
};
function badge(kind) { return BADGE[kind] || { label: kind || 'unknown', variant: 'ghost', icon: '' }; }

// model value → display label used in copy ("expanded with Sonnet", "Expanding note with Sonnet…").
// Values MUST stay byte-identical with the server's client gate (utils/aiSecurity.js
// gateClientModel with allowOpenAI, i.e. the Bedrock allow-list plus the two OpenAI
// aliases) — a renamed value is a silent 400.
const MODEL_LABEL = {
  'bedrock-sonnet': 'Sonnet', 'bedrock-opus-4-6': 'Opus',
  'openai-luna': 'Luna (OpenAI)', 'openai-sol': 'Sol (OpenAI)',
};
function modelLabel(m) { return MODEL_LABEL[m] || m; }

// BAA footnotes (verbatim from the handoff README §02 / §03).
const BAA_LOADING = 'No specific vitals or lab values are fabricated. Runs on a HIPAA-eligible model (AWS Bedrock, under BAA).';
const BAA_REVIEW = 'No specific vitals or labs fabricated. Runs on a BAA-covered model (AWS Bedrock).';

const ELATION_BASE = 'https://app.elationemr.com/patient/';
const COMPOSE_MAX = 10000; // server caps editedText at 10000 (server/routes/noteExpander.js)

// ── Inline icons (stroke-based, ~Lucide) ───────────────────────────
function IconCheck() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}
function IconExternal() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M15 3h6v6" /><path d="M10 14 21 3" />
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    </svg>
  );
}
function IconAlert() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
      <path d="M12 9v4" /><path d="M12 17h.01" />
    </svg>
  );
}

// One write-card with a toggle switch (review gate, right column).
function WriteCard({ label, detail, on, disabled, onToggle, children }) {
  return (
    <div className={'ne-write-card' + (on ? ' is-on' : '')}>
      <button type="button" role="switch" aria-checked={on} aria-label={label}
              className={'ne-toggle' + (on ? ' on' : '')}
              disabled={disabled} onClick={onToggle}>
        <span className="ne-toggle-knob" />
      </button>
      <div className="ne-write-copy">
        <div className="ne-write-label">{label}</div>
        {detail && <div className="ne-write-detail">{detail}</div>}
        {children}
      </div>
    </div>
  );
}

export function NoteExpanderPage() {
  // ---- Page view: draft inbox (default) or the style-prefs review ----
  const [view, setView] = useState('inbox');          // 'inbox' | 'prefs'
  // ---- Inbox (the only entry point) ----
  const [tab, setTab] = useState('all');              // 'all' | 'mine'
  const [inboxQ, setInboxQ] = useState('');
  const [rows, setRows] = useState([]);
  const [nextOffset, setNextOffset] = useState(null);
  const [mineAvailable, setMineAvailable] = useState(false);
  const [inboxBusy, setInboxBusy] = useState(false);
  const [inboxError, setInboxError] = useState('');
  const [expandedPreview, setExpandedPreview] = useState({}); // draftId -> bool (toggle full preview)
  const inboxDebounce = useRef(null);
  const inboxSeq = useRef(0);

  // ---- Master/detail selection + per-patient chart context ----
  const [selectedElationId, setSelectedElationId] = useState(null);
  const [contextByPatient, setContextByPatient] = useState({}); // elationId -> { loading, problems, medications, allergies, error }

  // ---- Model picker ----
  const [model, setModel] = useState('bedrock-sonnet');

  // ---- Expand → review → write modal flow (flowReducer, Task 3) ----
  // flow: 'inbox' | 'loading' | 'review' | 'writing' | 'done'
  const [flow, dispatch] = useReducer(flowReducer, {
    flow: 'inbox', activeDraftId: null, expansion: null, result: null, error: '',
    // carried alongside the reducer's own fields (reducer spreads ...state, so
    // these survive transitions): identity for the modal headers + Elation link.
    patientName: '', patientElationId: null, model: 'bedrock-sonnet',
  });

  // Editable review state derived from the Review reducer (problems/admins toggle cards + narrative).
  const [review, setReview] = useState(null);
  const [narrative, setNarrative] = useState('');
  const [refIds, setRefIds] = useState([]);
  // Pre-expand compose step (ephemeral — dropped on Cancel/close).
  const [composeText, setComposeText] = useState('');
  const [composeLoading, setComposeLoading] = useState(false);
  const [composeError, setComposeError] = useState(false);
  // Labels of the writes actually submitted on approve (for the confirmation checklist).
  const [committedWrites, setCommittedWrites] = useState([]);

  // Group the flat inbox rows into per-patient groups (first-seen order).
  const groups = useMemo(() => groupByPatient(rows), [rows]);

  // Load the inbox whenever tab changes (or on mount). Debounced search re-queries with q.
  async function loadInbox(scope, query, offset = 0) {
    const seq = ++inboxSeq.current;
    setInboxBusy(true); setInboxError('');
    try {
      const r = await ccmApi.noteExpanderInbox({ scope, q: query, offset });
      if (seq !== inboxSeq.current) return; // drop stale response
      setMineAvailable(!!r.mineAvailable);
      setRows(offset ? prev => prev.concat(r.drafts || []) : (r.drafts || []));
      setNextOffset(r.nextOffset != null ? r.nextOffset : null);
    } catch (e) {
      if (seq !== inboxSeq.current) return;
      setInboxError(e && e.body && e.body.disabled ? 'Note Expander is not enabled for this practice.' : 'Could not load the inbox.');
    } finally {
      if (seq === inboxSeq.current) setInboxBusy(false);
    }
  }

  // Initial load + reload on tab switch.
  useEffect(() => {
    loadInbox(tab, inboxQ.trim(), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Default-select the first group with something to expand (else the first group).
  useEffect(() => {
    if (!groups.length) { if (selectedElationId != null) setSelectedElationId(null); return; }
    const stillThere = groups.some(g => String(g.elationId) === String(selectedElationId));
    if (selectedElationId != null && stillThere) return;
    const firstExpandable = groups.find(g => g.toExpand > 0) || groups[0];
    setSelectedElationId(firstExpandable.elationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  // Fetch chart context for the selected patient (cached per elationId).
  useEffect(() => {
    const eid = selectedElationId;
    if (eid == null) return;
    if (contextByPatient[eid]) return; // already loaded / loading
    setContextByPatient(prev => ({ ...prev, [eid]: { loading: true } }));
    ccmApi.noteExpanderPatientContext(eid).then(r => {
      setContextByPatient(prev => ({
        ...prev,
        [eid]: { loading: false, problems: r.problems || [], medications: r.medications || [], allergies: r.allergies || [] },
      }));
    }).catch(() => {
      setContextByPatient(prev => ({ ...prev, [eid]: { loading: false, error: true, problems: [], medications: [], allergies: [] } }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedElationId]);

  function onInboxSearch(v) {
    setInboxQ(v); clearTimeout(inboxDebounce.current);
    inboxDebounce.current = setTimeout(() => loadInbox(tab, v.trim(), 0), 250);
  }

  function loadMore() {
    if (nextOffset == null) return;
    loadInbox(tab, inboxQ.trim(), nextOffset);
  }

  // Map an expand/commit failure to a user-facing message. 403 → disabled; 409 → reason.
  function expandErrorMessage(e) {
    if (e && e.status === 403 && e.body && e.body.disabled) return 'Note Expander is not enabled for this practice.';
    if (e && e.status === 409 && e.body && e.body.reason) {
      const reason = e.body.reason;
      if (reason === 'signed') return 'This note was already signed — nothing to expand.';
      if (reason === 'deleted') return 'This draft was deleted in Elation — nothing to expand.';
      if (reason === 'system') return 'This is a system/billing note — not expandable.';
      return `Cannot expand this draft (${reason}).`;
    }
    return 'Expansion failed.';
  }

  // Open the pre-expand compose step. The confirm gate for already-expanded
  // notes stays — it gates entry into compose. Fetches the draft's freeform text
  // to seed the editable box (loading/error states, like patient-context).
  async function openCompose(row) {
    const kind = row.classification && row.classification.kind;
    if (kind === 'expanded' && !window.confirm('This note looks already expanded — expand anyway?')) return;
    dispatch({
      type: 'compose', draftId: row.draftId,
      patientName: (row.patient && row.patient.name) || 'Patient',
      patientElationId: row.patient && row.patient.elationId,
      model,
    });
    setComposeText('');
    setComposeError(false);
    setComposeLoading(true);
    try {
      const r = await ccmApi.noteExpanderDraftText(row.draftId);
      setComposeText(r.text || '');
    } catch (e) {
      setComposeError(true);
    } finally {
      setComposeLoading(false);
    }
  }

  // Run the expansion with the (possibly edited) compose text. Drives loading →
  // review exactly like the old expand back-half; editedText feeds the model.
  async function runExpand() {
    const draftId = flow.activeDraftId;
    dispatch({ type: 'expand', draftId });
    try {
      const r = await ccmApi.noteExpanderExpandDraft(draftId, flow.model, composeText);
      setReview(Review.initReview(r));
      setNarrative(r.expandedNarrative);
      setRefIds(r.referencedDocumentIds || []);
      dispatch({ type: 'review', expansion: r });
    } catch (e) {
      dispatch({ type: 'expandError', message: expandErrorMessage(e) });
    }
  }

  // Approve & write — feeds the shared review payload, with the SAME field names
  // the original approve() used: { draftId, expandedNarrative, referencedDocumentIds, ...toCommitPayload }.
  async function approve() {
    if (flow.activeDraftId == null || !review) return;
    const payload = Review.toCommitPayload({ ...review, expandedNarrative: narrative });
    // Snapshot the human-readable labels of the writes being submitted (the
    // commit result only carries ids/errors). Replace is always first.
    const labels = ['Replace draft note in Elation'];
    for (const p of payload.problems) labels.push('Add to problem list — ' + (p.description || p.snomed));
    for (const a of payload.administrations) labels.push('Document in-office administration — ' + [a.drug, a.route].filter(Boolean).join(' '));
    setCommittedWrites(labels);
    dispatch({ type: 'approve' });
    try {
      const r = await ccmApi.noteExpanderCommitDraft({
        draftId: flow.activeDraftId,
        expandedNarrative: narrative,
        referencedDocumentIds: refIds,
        // Feedback-loop attribution: which model produced this narrative and
        // which style-prefs version (if any) shaped the prompt.
        model: flow.model || null,
        promptPrefsId: (flow.expansion && flow.expansion.promptPrefsId) || null,
        ...payload,
      });
      dispatch({ type: 'written', result: r });
    } catch (e) {
      // A thrown commit (500/network) returns to the REVIEW gate — not the inbox —
      // so the user's edited narrative survives and they can retry Approve.
      // (expandError is reserved for the EXPAND failure, which has no edits to keep.)
      dispatch({ type: 'writeError', message: expandErrorMessage(e) });
    }
  }

  // Close the flow back to the inbox (Esc / × / Cancel / Back to inbox).
  function closeFlow() {
    setReview(null); setNarrative(''); setRefIds([]); setCommittedWrites([]);
    setComposeText(''); setComposeError(false); setComposeLoading(false);
    dispatch({ type: 'reset' });
  }

  // Confirmation → Back to inbox: drop the committed row from the list ONLY when
  // the primary note write actually succeeded. On a failed note-write the draft
  // still exists in Elation (it wasn't replaced), so keep the row so it can be
  // retried from the inbox.
  function backToInboxFromDone() {
    const committed = flow.activeDraftId;
    const noteWritten = !!(flow.result && flow.result.note && flow.result.note.ok);
    if (committed != null && noteWritten) setRows(prev => prev.filter(x => x.draftId !== committed));
    closeFlow();
  }

  function openInElation() {
    if (flow.patientElationId == null) return;
    window.open(ELATION_BASE + flow.patientElationId + '/', '_blank');
  }

  // Esc closes the review gate (and any other open modal).
  useEffect(() => {
    if (flow.flow === 'inbox') return;
    const onKey = e => { if (e.key === 'Escape' && (flow.flow === 'review' || flow.flow === 'compose')) closeFlow(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flow.flow]);

  const selectedGroup = groups.find(g => String(g.elationId) === String(selectedElationId)) || null;
  const selectedCtx = selectedGroup ? contextByPatient[selectedGroup.elationId] : null;
  const overlayOpen = flow.flow !== 'inbox';

  return (
    <div className="note-expander-page">
      {/* ---- Toolbar: All/Mine filter + search + model ---- */}
      <div className="ne-toolbar">
        <div className="ne-filter-group" role="tablist" aria-label="Inbox scope">
          <button type="button" role="tab" aria-selected={tab === 'all'}
                  className={'filter-pill' + (tab === 'all' ? ' active' : '')}
                  onClick={() => setTab('all')}>All</button>
          {mineAvailable && (
            <button type="button" role="tab" aria-selected={tab === 'mine'}
                    className={'filter-pill' + (tab === 'mine' ? ' active' : '')}
                    onClick={() => setTab('mine')}>Mine</button>
          )}
        </div>
        <div className="ne-search">
          <svg className="ne-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
          </svg>
          <input value={inboxQ} placeholder="Filter by patient name…"
                 onChange={e => onInboxSearch(e.target.value)} aria-label="Filter inbox by patient" />
        </div>
        <label className="ne-model-pick">Model
          <select value={model} onChange={e => setModel(e.target.value)} aria-label="Expansion model">
            <option value="bedrock-sonnet">Sonnet</option>
            <option value="bedrock-opus-4-6">Opus</option>
            <option value="openai-luna">Luna (OpenAI)</option>
            <option value="openai-sol">Sol (OpenAI)</option>
          </select>
        </label>
        <button type="button"
                className={'filter-pill' + (view === 'prefs' ? ' active' : '')}
                aria-pressed={view === 'prefs'}
                onClick={() => setView(v => (v === 'prefs' ? 'inbox' : 'prefs'))}>
          Style prefs
        </button>
      </div>

      {view === 'prefs' && <NoteExpanderPrefsView />}

      {flow.error && <div className="ne-error">{flow.error}</div>}

      {/* ---- Master/detail inbox (dimmed while a modal is open) ---- */}
      <div className={'ne-split' + (overlayOpen ? ' is-dimmed' : '')}
           style={view === 'prefs' ? { display: 'none' } : undefined}
           aria-hidden={overlayOpen || view === 'prefs' ? 'true' : undefined}>
        {/* Left rail — patients */}
        <aside className="ne-rail">
          <div className="ne-rail-label">Patients · {groups.length}</div>
          {inboxError && <div className="ne-error">{inboxError}</div>}
          {inboxBusy && groups.length === 0 && <div className="ne-empty">Loading…</div>}
          {!inboxBusy && !inboxError && groups.length === 0 && (
            <div className="ne-empty">No unsigned drafts{inboxQ.trim() ? ' match that name.' : '.'}</div>
          )}
          {groups.map(g => {
            const active = String(g.elationId) === String(selectedElationId);
            return (
              <button key={String(g.elationId)} type="button"
                      className={'ne-rail-item' + (active ? ' is-active' : '')}
                      aria-current={active ? 'true' : undefined}
                      onClick={() => setSelectedElationId(g.elationId)}>
                <div className="ne-rail-name">{g.name}</div>
                <div className="ne-rail-counts">
                  {g.toExpand > 0 && <span className="ne-count-chip">{g.toExpand} to expand</span>}
                  {g.protectedCount > 0 && <span className="ne-count-muted">{g.protectedCount} protected</span>}
                </div>
              </button>
            );
          })}
          {nextOffset != null && (
            <button className="ne-load-more" onClick={loadMore} disabled={inboxBusy}>
              {inboxBusy ? 'Loading…' : 'Load more'}
            </button>
          )}
        </aside>

        {/* Right detail — selected patient */}
        <section className="ne-detail">
          {!selectedGroup && <div className="ne-empty ne-detail-empty">Select a patient to see their drafts.</div>}
          {selectedGroup && (
            <>
              <header className="ne-patient-header">
                <h2 className="ne-patient-name">{selectedGroup.name}</h2>
                <div className="ne-ctx">
                  <div className="ne-ctx-label">Chart context</div>
                  <div className="ne-ctx-chips">
                    {selectedCtx && selectedCtx.loading && <span className="ne-ctx-loading">Loading chart…</span>}
                    {selectedCtx && !selectedCtx.loading && selectedCtx.error && (
                      <span className="ne-ctx-loading">Chart context unavailable.</span>
                    )}
                    {selectedCtx && !selectedCtx.loading && !selectedCtx.error && (() => {
                      const probs = (selectedCtx.problems || []).map((p, i) => <span key={'p' + i} className="ne-ctx-chip">{p}</span>);
                      const meds = (selectedCtx.medications || []).map((m, i) => <span key={'m' + i} className="ne-ctx-chip">{m}</span>);
                      const algs = (selectedCtx.allergies || []).map((a, i) => <span key={'a' + i} className="ne-ctx-chip">Allergy: {a}</span>);
                      const all = probs.concat(meds, algs);
                      return all.length ? all : <span className="ne-ctx-loading">No documented chart context.</span>;
                    })()}
                  </div>
                </div>
              </header>

              <div className="ne-cards">
                {selectedGroup.drafts.map(row => {
                  const c = row.classification || {};
                  const kind = c.kind;
                  const b = badge(kind);
                  const isExpanded = kind === 'expanded';
                  const expandable = !!c.expandable;
                  const showFull = !!expandedPreview[row.draftId];
                  return (
                    <article key={row.draftId} className={'ne-card ne-card-' + (kind || 'unknown')}>
                      <div className="ne-card-head">
                        {row.provider && <span className="ne-card-provider">{row.provider}</span>}
                        <span className="ne-card-spacer" />
                        <span className={'pill' + (b.variant ? ' ' + b.variant : '')}>
                          {b.icon && <span className="ne-badge-icon" aria-hidden="true">{b.icon}</span>}{b.label}
                        </span>
                        {row.autoSweep && (() => {
                          const ab = badge(row.autoSweep.status);
                          return (
                            <span
                              className={'pill' + (ab.variant ? ' ' + ab.variant : '')}
                              title={row.autoSweep.error || ''}
                            >
                              {ab.icon && <span className="ne-badge-icon" aria-hidden="true">{ab.icon}</span>}{ab.label}
                            </span>
                          );
                        })()}
                        {row.date && <span className="ne-card-date">{row.date}</span>}
                      </div>
                      <div className={'ne-card-body' + (showFull ? ' is-full' : '')}
                           onClick={() => row.preview && setExpandedPreview(p => ({ ...p, [row.draftId]: !p[row.draftId] }))}
                           title={row.preview ? 'Click to toggle full preview' : undefined}>
                        {row.preview || '(empty draft — nothing to expand)'}
                      </div>
                      {isExpanded && c.reason && <div className="ne-card-reason">{c.reason}</div>}
                      <div className="ne-card-actions">
                        {expandable && !isExpanded && (
                          <button className="btn accent sm" onClick={() => openCompose(row)} disabled={overlayOpen}>
                            <span aria-hidden="true">→</span> Expand
                          </button>
                        )}
                        {isExpanded && (
                          <button className="btn sm ne-expand-anyway" onClick={() => openCompose(row)} disabled={overlayOpen}>
                            Expand anyway
                          </button>
                        )}
                        {!expandable && !isExpanded && (
                          <span className="ne-protected"><span aria-hidden="true">🔒</span> Protected — not expandable</span>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            </>
          )}
        </section>
      </div>

      {/* ════ Modal flow: loading / review / writing / done ════ */}

      {/* 01b — Compose (add to the note before expanding) */}
      {flow.flow === 'compose' && (
        <div className="modal-backdrop ne-backdrop">
          <div className="modal ne-modal ne-modal-compose" role="dialog" aria-modal="true" aria-label="Add to note before expanding">
            <div className="modal-head ne-review-head">
              <div>
                <h3>Add to these notes before expanding</h3>
                <div className="sub">{flow.patientName}</div>
              </div>
              <button className="btn icon ghost ne-close" aria-label="Close" onClick={closeFlow}>×</button>
            </div>
            <div className="modal-body">
              {composeLoading && <div className="ne-compose-status">Loading draft…</div>}
              {composeError && (
                <div className="ne-write-error" role="alert">
                  <IconAlert /> Couldn't load the draft text — you can still type notes below.
                </div>
              )}
              <textarea className="ne-compose" value={composeText}
                        onChange={e => setComposeText(e.target.value)}
                        disabled={composeLoading}
                        aria-label="Notes to expand"
                        placeholder="Add or edit the note here. This text is what gets expanded — nothing is saved to Elation until you Approve." />
              <div className={'ne-charcount' + (composeText.length > COMPOSE_MAX ? ' over' : '')}>
                {composeText.length} / {COMPOSE_MAX}
              </div>
            </div>
            <div className="modal-foot">
              <button className="btn ghost" onClick={closeFlow}>Cancel</button>
              <button className="btn accent" onClick={runExpand}
                      disabled={composeLoading || composeText.length > COMPOSE_MAX}>
                <span aria-hidden="true">→</span> Expand
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 02 — Expanding (loading) */}
      {flow.flow === 'loading' && (
        <div className="modal-backdrop ne-backdrop">
          <div className="modal ne-modal ne-modal-loading" role="dialog" aria-modal="true" aria-label="Expanding note">
            <div className="ne-loading-head">
              <span className="ne-spinner" aria-hidden="true" />
              <div>
                <div className="ne-loading-title">Expanding note</div>
                <div className="ne-loading-sub">{flow.patientName} · grounded in the full chart</div>
              </div>
            </div>
            <ol className="ne-modal-steps">
              <li className="is-done"><span className="ne-step-mark" aria-hidden="true"><IconCheck /></span>
                Reading chart — problems, meds, allergies, labs, prior note…</li>
              <li className="is-active"><span className="ne-step-mark ne-step-spin" aria-hidden="true" />
                Expanding note with {modelLabel(flow.model)}…</li>
            </ol>
            <p className="ne-modal-foot-note">{BAA_LOADING}</p>
          </div>
        </div>
      )}

      {/* 03 — Review gate */}
      {flow.flow === 'review' && review && (
        <div className="modal-backdrop ne-backdrop">
          <div className="modal ne-modal ne-modal-review" role="dialog" aria-modal="true" aria-label="Review before writing to chart">
            <div className="modal-head ne-review-head">
              <div>
                <h3>Review before writing to chart</h3>
                <div className="sub">{flow.patientName} · expanded with {modelLabel(flow.model)}</div>
              </div>
              <button className="btn icon ghost ne-close" aria-label="Close" onClick={closeFlow}>×</button>
            </div>

            <div className="ne-review-body">
              {/* Left — editable narrative */}
              <div className="ne-review-note">
                <div className="ne-review-label">Expanded visit note · editable</div>
                <textarea className="ne-note-textarea" value={narrative}
                          onChange={e => setNarrative(e.target.value)}
                          aria-label="Expanded visit note" />
              </div>

              {/* Right — chart writes */}
              <div className="ne-review-writes">
                <div className="ne-review-label">Will be written to Elation</div>

                {/* Expanded copy note — always on, not toggleable-off (it's the note itself). */}
                <WriteCard
                  label="Write expanded note to Elation" on disabled
                  detail="Creates the expanded note as a separate copy (attachments and vitals carried forward). The original stub stays in the chart until you delete it." />

                {/* Problems (newly-implied diagnoses) */}
                {review.problems.map((p, i) => {
                  if (p.status === 'manual') {
                    return (
                      <div key={'p' + i} className="ne-write-card is-manual">
                        <div className="ne-write-copy">
                          <div className="ne-write-label">{p.term}</div>
                          <div className="ne-write-detail">No matching code — add this diagnosis manually in Elation.</div>
                        </div>
                      </div>
                    );
                  }
                  if (p.status === 'options') {
                    const label = 'Add to problem list — ' + (p.chosenName || p.term);
                    return (
                      <WriteCard key={'p' + i} label={label}
                                 detail={p.committable ? 'SNOMED ' + p.chosenSnomed + ' → Elation derives the ICD-10 (you confirm). Not currently documented.' : 'Pick a concept below to add this diagnosis.'}
                                 on={p.checked} disabled={!p.committable}
                                 onToggle={() => setReview(Review.toggleProblem(review, i))}>
                        <select className="ne-write-select" value={p.chosenSnomed || ''}
                                onChange={e => setReview(Review.chooseOption(review, i, e.target.value))}
                                aria-label={'Concept for ' + p.term}>
                          <option value="">Choose concept…</option>
                          {p.options.map(o => (
                            <option key={o.snomed} value={o.snomed}>{o.name} → {o.icd10Preview || '?'}</option>
                          ))}
                        </select>
                      </WriteCard>
                    );
                  }
                  // codeable
                  return (
                    <WriteCard key={'p' + i} label={'Add to problem list — ' + p.description}
                               detail={'SNOMED ' + p.snomed + ' → Elation derives ICD-10 ' + p.icd10 + ' (you confirm). Not currently documented.'}
                               on={p.checked} disabled={!p.committable}
                               onToggle={() => setReview(Review.toggleProblem(review, i))} />
                  );
                })}

                {/* Administrations (one-time, in-office) */}
                {review.admins.map((a, i) => {
                  const label = 'Document in-office administration — ' + [a.drug, a.route].filter(Boolean).join(' ');
                  const blocked = a.allergyConflict && !a.acked;
                  return (
                    <WriteCard key={'a' + i} label={label}
                               detail={a.isVaccine ? 'One-time documented immunization; appears in the immunization record.' : 'One-time documented medication; appears in Meds Hx.'}
                               on={a.checked} disabled={blocked}
                               onToggle={() => setReview(Review.toggleAdmin(review, i))}>
                      {a.allergyConflict && !a.acked && (
                        <div className="ne-allergy-warn">
                          ⚠ Allergy conflict on file.
                          <button type="button" className="ne-ack-btn" onClick={() => setReview(Review.ackAllergy(review, i))}>Acknowledge</button>
                        </div>
                      )}
                    </WriteCard>
                  );
                })}

                <div className="ne-review-banner">
                  You stay the final reviewer. The note opens in Elation for you to <strong>review and sign</strong> — nothing is ever auto-signed.
                </div>
              </div>
            </div>

            {flow.error && (
              <div className="ne-write-error" role="alert">
                <IconAlert /> Couldn’t write to chart — {flow.error} Your edits are kept; press “Approve &amp; write to chart” to try again.
              </div>
            )}
            <div className="modal-foot ne-review-foot">
              <p className="ne-modal-foot-note">{BAA_REVIEW}</p>
              <div className="ne-review-actions">
                <button className="btn" onClick={closeFlow}>Cancel</button>
                <button className="btn accent" onClick={approve}>
                  <IconCheck /> Approve &amp; write to chart
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 04a — Writing (transient) */}
      {flow.flow === 'writing' && (
        <div className="modal-backdrop ne-backdrop">
          <div className="modal ne-modal ne-modal-writing" role="dialog" aria-modal="true" aria-label="Writing to Elation">
            <span className="ne-spinner" aria-hidden="true" />
            <div className="ne-writing-title">Writing to Elation…</div>
            <div className="ne-writing-sub">Creating note, updating problem list &amp; meds.</div>
          </div>
        </div>
      )}

      {/* 04 — Written-to-chart confirmation (or commit-failure report).
          The route returns HTTP 200 even on write failures (note.ok:false,
          per-item ok:false), so this modal must branch on the actual result —
          a failed note write must NOT look like a green success. */}
      {flow.flow === 'done' && flow.result && (() => {
        const r = flow.result;
        const noteOk = !!(r.note && r.note.ok);
        const problems = r.problems || [];
        const administrations = r.administrations || [];
        // committedWrites = [Replace, ...problems, ...admins]; align ok flags by order.
        const probLabels = committedWrites.slice(1, 1 + problems.length);
        const adminLabels = committedWrites.slice(1 + problems.length);

        // Succeeded writes (only meaningful to list when each ok:true).
        const applied = [];
        if (noteOk) applied.push(committedWrites[0] || 'Replace draft note in Elation');
        problems.forEach((x, i) => { if (x.ok && probLabels[i]) applied.push(probLabels[i]); });
        administrations.forEach((x, i) => { if (x.ok && adminLabels[i]) applied.push(adminLabels[i]); });

        // Failures — surfaced whether or not the note itself wrote.
        const failures = [];
        problems.forEach((x, i) => {
          if (!x.ok) failures.push({ label: (probLabels[i] || ('Add to problem list — ' + (x.term || 'diagnosis'))), error: x.error });
        });
        administrations.forEach((x, i) => {
          if (!x.ok) failures.push({ label: (adminLabels[i] || ('Document in-office administration — ' + (x.drug || 'medication'))), error: x.error });
        });

        const dxOk = problems.filter(x => x.ok).length;
        const adminOkCount = administrations.filter(x => x.ok).length;
        const dxFail = problems.length - dxOk;
        const adminFail = administrations.length - adminOkCount;
        // Summary counts that INCLUDE failures (e.g. "2 of 3 diagnoses added · 1 failed").
        const summaryBits = [];
        if (problems.length) summaryBits.push(`${dxOk} of ${problems.length} diagnos${problems.length === 1 ? 'is' : 'es'} added${dxFail ? ` · ${dxFail} failed` : ''}`);
        if (administrations.length) summaryBits.push(`${adminOkCount} of ${administrations.length} administration${administrations.length === 1 ? '' : 's'} documented${adminFail ? ` · ${adminFail} failed` : ''}`);
        const n = applied.length;

        return (
          <div className="modal-backdrop ne-backdrop">
            <div className="modal ne-modal ne-modal-done" role="dialog" aria-modal="true"
                 aria-label={noteOk ? 'Written to chart' : 'Couldn’t write to chart'}>
              {noteOk ? (
                <div className="ne-done-head">
                  <span className="ne-success" aria-hidden="true"><IconCheck /></span>
                  <div>
                    <div className="ne-done-title">Written to chart</div>
                    <div className="ne-done-sub">{flow.patientName}{summaryBits.length ? ' · ' + summaryBits.join(' · ') : ` · ${n} update${n === 1 ? '' : 's'} applied`}</div>
                  </div>
                </div>
              ) : (
                <div className="ne-done-head">
                  <span className="ne-fail" aria-hidden="true"><IconAlert /></span>
                  <div>
                    <div className="ne-done-title">Couldn’t write to chart</div>
                    <div className="ne-done-sub">{flow.patientName} · the note was not written{summaryBits.length ? ' · ' + summaryBits.join(' · ') : ''}</div>
                  </div>
                </div>
              )}

              {!noteOk && r.note && r.note.error && (
                <div className="ne-write-error" role="alert">
                  <IconAlert /> {r.note.error}
                </div>
              )}

              {applied.length > 0 && (
                <ul className="ne-done-list">
                  {applied.map((label, i) => (
                    <li key={i}><span className="ne-done-check" aria-hidden="true"><IconCheck /></span>{label}</li>
                  ))}
                </ul>
              )}

              {failures.length > 0 && (
                <ul className="ne-done-list ne-done-fails">
                  {failures.map((f, i) => (
                    <li key={'f' + i}>
                      <span className="ne-fail-mark" aria-hidden="true"><IconAlert /></span>
                      <span>{f.label}{f.error ? <span className="ne-fail-reason"> — {f.error}</span> : null}</span>
                    </li>
                  ))}
                </ul>
              )}

              {noteOk && (
                <div className="ne-amber">
                  Open the note in Elation to <strong>review and sign</strong>. Newly-added diagnoses still need ICD-10 confirmation. Nothing was auto-signed.
                </div>
              )}

              <div className="ne-done-actions">
                <button className="btn" onClick={backToInboxFromDone}>Back to inbox</button>
                {noteOk && (
                  <button className="btn primary" onClick={openInElation}>
                    Open in Elation <IconExternal />
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
